import api, { handleResponse, handleCustomResponse } from "../queries/api";
import {
  UserEmailModel,
  SignInDetailsModel,
  SignResponseModel,
  ResetPasswordDetailModel,
  SignUpDetailsModel,
  ChangePasswordDetailModelAuth,
  LogoutFromAllDevicesModel,
  PublicSignUpModel,
} from "../types/auth";
import { NotificationModel } from "../components/modal/SendNotification";
import { BannerMessage, CustomResponse, UserData } from "../types/user";

// Base paths
const getBasePath = () => "/user";
const getClientBasePath = () => "/client";

// Authentication endpoints
export async function signIn(
  signInDetails: SignInDetailsModel,
): Promise<CustomResponse<SignResponseModel>> {
  return api.post(`${getBasePath()}/login`, signInDetails).then(handleResponse);
}

export async function signUp(
  signUpDetailsModel: SignUpDetailsModel,
): Promise<CustomResponse<SignResponseModel>> {
  return api
    .post(`${getBasePath()}/signUp`, signUpDetailsModel)
    .then(handleResponse);
}

export async function publicSignUp(
  signUpDetailsModel: PublicSignUpModel,
): Promise<CustomResponse<any>> {
  return api
    .post(`${getBasePath()}/publicSignUp`, signUpDetailsModel)
    .then(handleResponse);
}

// Email verification endpoints
export async function verifyEmailOtp(payload: {
  email: string;
  otp: string;
}): Promise<CustomResponse<any>> {
  return api
    .post(`${getBasePath()}/verifyEmailOtp`, payload)
    .then(handleResponse);
}

export async function resendEmailOtp(
  email: string,
): Promise<CustomResponse<any>> {
  return api
    .post(`${getBasePath()}/resendEmailOtp`, { email })
    .then(handleResponse);
}

// Password management endpoints
export async function forgotPassword(
  email: string,
): Promise<CustomResponse<string>> {
  return api
    .post(`${getBasePath()}/forgotPassword`, { email })
    .then(handleResponse);
}

export async function resetPassword(
  resetPasswordDetail: ResetPasswordDetailModel,
): Promise<CustomResponse<string>> {
  return api
    .post(`${getBasePath()}/resetPassword`, resetPasswordDetail, {
      headers: { accessToken: `Bearer ${resetPasswordDetail.jwt}` },
    })
    .then(handleResponse);
}

export async function changePassword(
  resetPasswordDetail: ResetPasswordDetailModel,
): Promise<CustomResponse<string>> {
  return api
    .post(`${getBasePath()}/changePassword`, resetPasswordDetail)
    .then(handleResponse);
}

export async function changeNewPassword(
  resetPasswordDetail: ResetPasswordDetailModel,
): Promise<CustomResponse<string>> {
  return api
    .post(`${getBasePath()}/changeNewPassword`, resetPasswordDetail)
    .then(handleResponse);
}

export async function changePasswordAuth(
  changePasswordAuth: ChangePasswordDetailModelAuth,
): Promise<CustomResponse<SignResponseModel>> {
  return api
    .post(`${getBasePath()}/changePasswordAuth`, changePasswordAuth)
    .then(handleResponse);
}

// Session management
export async function logOutFromAllDevices(
  logoutFromAllDevices: LogoutFromAllDevicesModel,
): Promise<CustomResponse<SignResponseModel>> {
  return api
    .post(`${getBasePath()}/logoutFromAllDevices`, logoutFromAllDevices)
    .then(handleResponse);
}

// User management endpoints
export async function getAllUsers(): Promise<CustomResponse<UserData[]>> {
  return api.get(`${getBasePath()}/all`).then(handleResponse);
}

export async function deleteUser(userId: string): Promise<CustomResponse<any>> {
  return api.delete(`${getBasePath()}/${userId}`).then(handleResponse);
}

export async function generateCredentials(
  userDetails: UserEmailModel,
): Promise<CustomResponse<any>> {
  return api
    .post(`${getBasePath()}/generateCredentials`, userDetails)
    .then(handleResponse);
}

export async function deactivateUser(
  userDetails: UserEmailModel,
): Promise<CustomResponse<any>> {
  return api
    .put(`${getBasePath()}/deactivate`, {
      email: userDetails.email,
      deactivated: true,
    })
    .then(handleResponse);
}

export async function activateUser(
  userDetails: UserEmailModel,
): Promise<CustomResponse<any>> {
  return api
    .put(`${getBasePath()}/activate`, {
      email: userDetails.email,
      deactivated: false,
    })
    .then(handleResponse);
}

// Message and notification endpoints
export async function sendMessage(
  message: NotificationModel,
): Promise<CustomResponse<SignResponseModel>> {
  return api.post(`${getBasePath()}/message`, message).then(handleResponse);
}

export async function getMessage(): Promise<any> {
  return api.get(`${getBasePath()}/message`).then((res) => res.data.data);
}

export async function sendBannerMessage(
  message: BannerMessage,
): Promise<CustomResponse<SignResponseModel>> {
  return api
    .put(`${getClientBasePath()}/bannerMessage`, message)
    .then(handleResponse);
}
